import type {
  DocSection,
  DocsTree,
  DocSummary,
  FlatDoc,
} from '../model/docs.type';

/**
 * 섹션 대표 문서의 slug. **서버 `SECTION_INDEX_SLUG` 와 같아야 한다.**
 *
 * 이 slug 를 가진 문서는 주소에서 세 번째 세그먼트가 접혀 `/docs/<section>` 이 된다.
 * FAQ 처럼 문서가 사실상 하나뿐인 섹션에서 `/docs/faq/faq` 를 피하고, 섹션 주소를
 * 실제로 쓰이게 만든다.
 */
export const SECTION_INDEX_SLUG = 'index';

/**
 * 링크 규칙. 서버가 `doc.path` 를 이미 이 규칙으로 조립해서 주므로 보통은 그 값을
 * 쓰고, 응답 객체 없이 경로를 만들어야 할 때만 이걸 쓴다(리다이렉트, 저장 후 이동).
 */
export function docPath(sectionSlug: string, docSlug: string): string {
  return docSlug === SECTION_INDEX_SLUG
    ? `/docs/${sectionSlug}`
    : `/docs/${sectionSlug}/${docSlug}`;
}

/** 섹션 대표 문서. 없으면 undefined — 그 섹션의 `/docs/<section>` 은 여전히 리다이렉트다. */
export function indexDocOf(
  section: DocSection | undefined,
): DocSummary | undefined {
  return section?.docs.find((doc) => doc.slug === SECTION_INDEX_SLUG);
}

/**
 * 목록(사이드바·인덱스 카드)에 줄로 나열할 문서.
 *
 * 대표 문서는 섹션 제목 자체가 링크라 목록에서 뺀다 — 안 그러면 `FAQ` 아래
 * `FAQ` 가 한 번 더 붙는다.
 */
export function listedDocsOf(section: DocSection): DocSummary[] {
  return section.docs.filter((doc) => doc.slug !== SECTION_INDEX_SLUG);
}

/**
 * 트리를 매니페스트 순서 그대로 평탄화. 서버가 이미 정렬해서 주므로 재정렬하지 않는다
 * (섹션 sortOrder → 문서 sortOrder). 이 순서가 곧 이전/다음 순서다.
 */
export function flattenDocs(tree: DocsTree | undefined): FlatDoc[] {
  if (!tree) return [];
  return tree.sections.flatMap((section) =>
    section.docs.map((doc) => ({ section, doc })),
  );
}

export function findDoc(
  tree: DocsTree | undefined,
  section: string | undefined,
  slug: string | undefined,
): FlatDoc | undefined {
  if (!tree || !section || !slug) return undefined;
  return flattenDocs(tree).find(
    (item) => item.section.slug === section && item.doc.slug === slug,
  );
}

export function findSection(
  tree: DocsTree | undefined,
  slug: string | undefined,
): DocSection | undefined {
  if (!tree || !slug) return undefined;
  return tree.sections.find((section) => section.slug === slug);
}

/**
 * 옛 경로면 현재 경로를, 아니면 undefined.
 *
 * 404 를 내기 전에 반드시 먼저 본다 — 배포된 데스크톱 앱이 옛 slug 로 들어온다.
 * 표는 트리 응답에 같이 실려 오므로 죽은 경로마다 서버에 왕복하지 않는다.
 */
export function resolveRedirect(
  tree: DocsTree | undefined,
  section: string | undefined,
  slug: string | undefined,
): string | undefined {
  if (!tree || !section || !slug) return undefined;
  const from = `${section}/${slug}`;
  return tree.redirects.find((redirect) => redirect.from === from)?.to;
}

/** 매니페스트 전체 순서 기준 이전/다음. 섹션 경계를 넘는 것은 의도다. */
export function getSiblings(
  tree: DocsTree | undefined,
  current: FlatDoc | undefined,
): { prev?: FlatDoc; next?: FlatDoc } {
  if (!tree || !current) return {};
  const flat = flattenDocs(tree);
  const index = flat.findIndex((item) => item.doc.id === current.doc.id);
  if (index < 0) return {};
  return { prev: flat[index - 1], next: flat[index + 1] };
}

/**
 * 제목·요약·키워드·섹션명 부분일치. 띄어쓰기는 양쪽 다 무시한다 —
 * "통합 내역"과 "통합내역", "내역작성"과 "내역 작성"을 같게 본다.
 *
 * 문서 100개 규모에선 이걸로 충분하다 — 검색 라이브러리도, 서버 왕복도 없다.
 * 본문(heading)까지 훑으려면 서버 전문검색이 필요하고, 그건 별도 작업이다.
 */
export function searchDocs(
  tree: DocsTree | undefined,
  query: string,
  limit = 8,
): FlatDoc[] {
  const needle = compact(query);
  if (!needle || !tree) return [];
  const words = query.split(/\s+/).map(compact).filter(Boolean);

  const scored = flattenDocs(tree)
    .map((item) => ({ item, score: scoreDoc(item, needle, words) }))
    .filter((hit) => hit.score >= 0);

  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).map((hit) => hit.item);
}

/** 낮을수록 상위. 음수는 "결과 아님". */
const SCORE = {
  TITLE_PREFIX: 0,
  TITLE_MATCH: 1,
  FIELD_MATCH: 2,
  ALL_WORDS: 3,
  NO_MATCH: -1,
} as const;

/** 비교용 정규형 — 소문자, 공백 제거. */
function compact(text: string): string {
  return text.toLowerCase().replace(/\s+/g, '');
}

/**
 * 제목 접두일치 > 제목 포함 > 요약·섹션명·키워드 중 한 칸에 포함 > 낱말이 여러 칸에 흩어져 모두 있음.
 * 칸은 따로 비교한다. 이어 붙여 공백을 지우면 키워드 `통합`·`내역` 이 "통합내역"으로 잘못 걸린다.
 */
function scoreDoc(
  { section, doc }: FlatDoc,
  needle: string,
  words: string[],
): number {
  const title = compact(doc.title);
  if (title.startsWith(needle)) return SCORE.TITLE_PREFIX;
  if (title.includes(needle)) return SCORE.TITLE_MATCH;

  const fields = [
    title,
    compact(doc.summary),
    compact(section.title),
    ...doc.keywords.map(compact),
  ];
  if (fields.some((field) => field.includes(needle))) return SCORE.FIELD_MATCH;

  const allWords =
    words.length > 1 &&
    words.every((word) => fields.some((field) => field.includes(word)));
  return allWords ? SCORE.ALL_WORDS : SCORE.NO_MATCH;
}

/** 사이드바·인덱스에서 "문서 N개" 표기. */
export function countDocs(tree: DocsTree | undefined): number {
  return flattenDocs(tree).length;
}

/** 첫 문서 — `/docs/:section` 진입 시 여기로 보낸다. */
export function firstDocOf(section: DocSection | undefined): DocSummary | undefined {
  return section?.docs[0];
}
